/** One Stores report; its filters live in the URL so a report can be bookmarked or shared. */
import {
  STORES_REPORTS,
  canOpenStoresReport,
  isStoresReportKey,
} from "@nexseed/shared";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { ListPageSkeleton } from "@/components/app/skeletons";
import { useAuth } from "@/features/auth/store";
import { useFacilities } from "@/features/auth/use-facilities";
import { StoresReportPage } from "@/features/stores/components/reports-pages";
import { DATE_MODES } from "@/lib/dates";

const id = z.coerce.number().int().positive().optional().catch(undefined);
const search = z.object({
  asOn: z.iso.date().optional().catch(undefined),
  dates: z.enum(DATE_MODES).optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  classificationId: id,
  itemId: id,
  condition: z.enum(["good", "damage"]).optional().catch(undefined),
  withSloc: z.boolean().optional().catch(undefined),
  party: z.string().max(40).optional().catch(undefined),
  partyText: z.string().max(100).optional().catch(undefined),
  direction: z.enum(["out", "in"]).optional().catch(undefined),
  whId: id,
  binId: id,
  subBinId: id,
});

export const Route = createFileRoute("/_app/stores/reports/$key")({
  validateSearch: search,
  beforeLoad: ({ params }) => {
    const can = useAuth.getState().can;
    if (
      !isStoresReportKey(params.key) ||
      !canOpenStoresReport(STORES_REPORTS[params.key], can)
    )
      throw redirect({ to: "/stores/reports" });
  },
  component: Page,
});

function Page() {
  const { key } = Route.useParams();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { selected, ready } = useFacilities();
  if (!isStoresReportKey(key)) return null;
  if (!ready) return <ListPageSkeleton />;
  return (
    <StoresReportPage
      key={key}
      reportKey={key}
      facilityId={selected?.id}
      search={s}
      onSearch={(next) =>
        void navigate({ search: (prev) => ({ ...prev, ...next }) })
      }
    />
  );
}
